import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  Box,
} from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import useProfile from 'hooks/useProfile';
import { useTranslation } from 'next-i18next';
import { useCallback, useRef, useState, useEffect } from 'react';
import type { FC } from 'react';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';

import { useAppDispatch } from 'redux-store';
import addDeviceThunk from 'redux-store/thunks/devices/addDeviceThunk';
import getAllDevicesThunk from 'redux-store/thunks/devices/getAllDevicesThunk';
import getOrganizationByIdThunk from 'redux-store/thunks/organizations/getOrganizationByIdThunk';

import type { AddDevicesToOrganizationModalStageProps } from '..';

export interface AddNewDeviceProps
  extends AddDevicesToOrganizationModalStageProps {
  organizationId: number | null;
}

const AddNewDevice: FC<AddNewDeviceProps> = ({
  open,
  organizationId,
  setState,
}) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const profile = useProfile();

  const [serialNumber, setSerialNumber] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const serialInputRef = useRef<HTMLInputElement>(null);

  const handleAddDevice = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      if (
        isLoading ||
        serialNumber.length < 6 ||
        !password.length ||
        !organizationId
      ) {
        return;
      }

      try {
        setLoading(true);
        const result = await dispatch(
          addDeviceThunk({ serial: serialNumber, password }),
        ).unwrap();

        if (result.success) {
          if (profile) {
            await dispatch(getAllDevicesThunk({ userId: profile.id }));
          }
          await dispatch(
            getOrganizationByIdThunk({ organizationId: organizationId }),
          ).unwrap();

          setState(prev => ({
            ...prev,
            devices: [...prev.devices],
            newDeviceSerial: serialNumber,
            stage: 'devices',
          }));

          setSerialNumber('');
          setPassword('');
        } else {
          const errorMsg = i18n.exists(result.error)
            ? t(result.error)
            : result.error;
          setError(errorMsg);
        }
      } catch (e) {
        Sentry.captureException(e, {
          extra: { serialNumber },
          tags: { page: 'AddNewDeviceModal' },
        });
      } finally {
        setLoading(false);
      }
    },
    [
      serialNumber,
      isLoading,
      password,
      organizationId,
      t,
      dispatch,
      profile,
      setState,
      i18n,
    ],
  );

  useEffect(() => {
    if (open) {
      serialInputRef.current?.focus();
    }
  }, [open]);

  const validSerialNumber =
    serialNumber.length >= 6 && serialNumber.length <= 10;
  const validPassword = password.length > 0;
  const valid = validSerialNumber && validPassword && !error;

  const handleNavigateToInstructions = (): void => {
    setState(prev => ({
      ...prev,
      stage: 'instructions',
    }));
  };

  return (
    <Box height="100%" width="100%" sx={{ overflowY: 'auto' }}>
      <form onSubmit={() => handleAddDevice}>
        <Box mb={3}>
          <Typography
            variant="h2"
            textAlign="center"
            data-testid="new-device-title"
            mb={2}
          >
            {t('common:add_new_device')}
          </Typography>
          <Typography variant="body1" textAlign="center">
            {t('common:add_new_device_description')}
          </Typography>
          <Typography
            textAlign="center"
            variant="body1"
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={handleNavigateToInstructions}
          >
            {t('common:add_device_info.title')}
          </Typography>
        </Box>
        <Box mb={2}>
          <TextField
            label={t('common:serial_number')}
            name="serial"
            value={serialNumber}
            onChange={(e): void => {
              setError(null);
              setSerialNumber(e.target.value);
            }}
            type="text"
            autoComplete="off"
            fullWidth
            inputProps={{
              maxLength: 10,
              'data-testid': 'new-device-serial',
            }}
            variant="filled"
            disabled={isLoading}
            inputRef={serialInputRef}
          />
        </Box>
        <Box mb={4}>
          <TextField
            label={t('common:password')}
            name="password"
            value={password}
            onChange={(e): void => {
              setError(null);
              setPassword(e.target.value);
            }}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={(): void => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
              inputProps: { 'data-testid': 'new-device-password' },
            }}
            fullWidth
            variant="filled"
            disabled={isLoading}
          />
        </Box>
        <ErrorText error={error} />
        <Box width="100%" display="flex" flexDirection="row" mt={2} gap={2}>
          <Button
            fullWidth
            variant="flat"
            type="submit"
            onClick={handleAddDevice}
            disabled={!valid || isLoading}
            size="large"
            data-testid="submit"
          >
            {t('common:add')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default AddNewDevice;
